import { HeaderItem } from '../../model/HeaderItem';
import { MasterDataLabelTextHelper } from '../labelTextHelper/MasterDataLabelTextHelper';
import { ImportHeaderHelper } from '../ImportHeaderHelper';

// Master Data Type Configuration
export interface MasterDataConfig {
    apiPath: string;
    codeField: string;
    nameField: string;
    displayName: string;
    displayNamePlural: string;
    responseListKey: string;
}

export const MASTER_DATA_CONFIG: Record<string, MasterDataConfig> = {
    currency: {
        apiPath: 'currency',
        codeField: 'currencyCode',
        nameField: 'currencyName',
        displayName: 'Currency',
        displayNamePlural: 'Currencies',
        responseListKey: 'currencies'
    },
    marketplace: {
        apiPath: 'marketplace',
        codeField: 'marketplaceCode',
        nameField: 'marketplaceName',
        displayName: 'Marketplace',
        displayNamePlural: 'Marketplaces',
        responseListKey: 'marketplaces'
    },
    productCategory: {
        apiPath: 'productCategory',
        codeField: 'categoryCode',
        nameField: 'categoryName',
        displayName: 'Product Category',
        displayNamePlural: 'Product Categories',
        responseListKey: 'categories'
    }
};

export const MasterDataHeaderHelper = {
    /**
     * Get table headers for a specific master data type
     */
    getHeadersForType: (type: string): HeaderItem[] => {
        const config = MASTER_DATA_CONFIG[type];
        if (!config) {
            throw new Error(`Unknown master data type: ${type}`);
        }

        return [
            { text: MasterDataLabelTextHelper.commonLabels.code, value: config.codeField },
            { text: MasterDataLabelTextHelper.commonLabels.name, value: config.nameField },
            { text: MasterDataLabelTextHelper.commonLabels.active, value: 'isActive' },
            { text: MasterDataLabelTextHelper.commonLabels.dateCreateAt, value: 'dateCreateAt', format: 'datetime' },
            { text: MasterDataLabelTextHelper.commonLabels.dateModifyAt, value: 'dateModifyAt', format: 'datetime' },
            // define in here due to watch() in MasterData.vue
            // need to filter out 'action' column from CSV export
            { text: MasterDataLabelTextHelper.commonLabels.action, value: 'action' }
        ];
    },

    /**
     * Get action header item
     */
    getActionHeaderItem: (): HeaderItem => {
        return { text: MasterDataLabelTextHelper.commonLabels.action, value: 'action' };
    },

    /**
     * Get CSV export headers for a specific master data type
     * Derives from getHeadersForType() to maintain consistency
     */
    getCsvHeaderForType: (type: string): any[] => {
        // Filter out 'action' column from CSV export
        return MasterDataHeaderHelper.getHeadersForType(type)
            .filter((item) => item.value !== 'actions')
            .map((item) => ({ key: item.value, label: item.text }));
    },

    /**
     * Get import fields for CSV import mapping
     */
    getImportFieldsForType: (type: string): any => {
        const config = MASTER_DATA_CONFIG[type];
        if (!config) {
            throw new Error(`Unknown master data type: ${type}`);
        }

        return {
            [config.codeField]: {
                label: MasterDataLabelTextHelper.commonLabels.code,
                required: true
            },
            [config.nameField]: {
                label: MasterDataLabelTextHelper.commonLabels.name,
                required: true
            }
        };
    },

    /**
     * Get import headers for CSV preview (data fields only, no status/result/error)
     */
    getImportHeadersForType: (type: string): HeaderItem[] => {
        const config = MASTER_DATA_CONFIG[type];
        if (!config) {
            throw new Error(`Unknown master data type: ${type}`);
        }

        return [
            { text: MasterDataLabelTextHelper.commonLabels.code, value: config.codeField },
            { text: MasterDataLabelTextHelper.commonLabels.name, value: config.nameField }
        ];
    }
};
